import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/redux/store";
import { Button } from "@/shared/components/ui/Button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/Dialog";

const domain = window.location.hostname ?? "frontend.dev.prodata.media";
const WEBSOCKET_CONNECTION_URL = `wss://${domain}/wss/`;
const ADMIN_LOGIN_NOTIFICATION_TOPIC = "__NOTIFICATIONS_ADMIN_LOGIN__";

interface LoginNotificationPayload {
	user_name?: string;
	user_email?: string;
	company_name?: string;
	is_jason_exclusive: "Y" | "N";
	message?: string;
	recipient_id: number;
}

export default function LoginNotificationListener() {
	const sessionUser = useAppSelector(state => state.sessionUser);
	const [notification, setNotification] = useState<LoginNotificationPayload | null>(null);
	const wsRef = useRef<WebSocket | null>(null);
	const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);

	const isAdmin = sessionUser.is_admin === "Y";
	const isEnabled = sessionUser.is_user_login_notification !== "N";

	useEffect(() => {
		if (!isAdmin || !isEnabled) {
			setNotification(null);
			if (reconnectTimeout.current) {
				clearTimeout(reconnectTimeout.current);
				reconnectTimeout.current = null;
			}
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
			return;
		}

		const connectWebSocket = () => {
			if (wsRef.current) return;
			const ws = new WebSocket(WEBSOCKET_CONNECTION_URL, ["wamp"]);

			ws.onopen = () => {
				ws.send(JSON.stringify([5, ADMIN_LOGIN_NOTIFICATION_TOPIC]));
			};

			ws.onmessage = event => {
				try {
					const response = JSON.parse(event.data);
					const data =
						Array.isArray(response) ?
							response.find((item: { category?: string }) => item?.category === ADMIN_LOGIN_NOTIFICATION_TOPIC)
						:	response;

					if (!data || data.category !== ADMIN_LOGIN_NOTIFICATION_TOPIC) return;

					const payload: LoginNotificationPayload = data.payload ? JSON.parse(data.payload) : data;

					if (+payload.recipient_id !== +sessionUser.id) return;

					setNotification(payload);
				} catch (err) {
					console.error("Error processing admin login notification:", err);
				}
			};

			ws.onerror = err => {
				console.error("Login notification WebSocket error:", err);
			};

			ws.onclose = event => {
				console.warn(`Login notification WebSocket closed: Code ${event.code}`);
				wsRef.current = null;
				if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
				reconnectTimeout.current = setTimeout(connectWebSocket, 5000);
			};

			wsRef.current = ws;
		};

		connectWebSocket();

		return () => {
			if (reconnectTimeout.current) {
				clearTimeout(reconnectTimeout.current);
				reconnectTimeout.current = null;
			}
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
		};
	}, [isAdmin, isEnabled, sessionUser.agency_id, sessionUser.id]);

	const notificationMessage = notification?.message ?? "";

	return (
		<Dialog open={Boolean(notification)} onOpenChange={open => !open && setNotification(null)}>
			<DialogContent hideCloseButton>
				<DialogHeader>
					<DialogTitle className="mb-1 text-red-600">⚠️ User Login Notification</DialogTitle>
					{notificationMessage && <DialogDescription>{notificationMessage}</DialogDescription>}
				</DialogHeader>
				{(notification?.user_name || notification?.company_name) && (
					<div className="bg-muted/50 space-y-2 rounded-md p-3 text-sm">
						{notification.user_name && (
							<p>
								<span className="font-medium">Name:</span> {notification.user_name}
							</p>
						)}
						{notification.user_email && (
							<p>
								<span className="font-medium">Email:</span> {notification.user_email}
							</p>
						)}
						{notification.company_name && (
							<p>
								<span className="font-medium">Company:</span> {notification.company_name}
							</p>
						)}
					</div>
				)}
				<DialogFooter>
					<Button type="button" onClick={() => setNotification(null)}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
